// ==========================================================================
// i18n for the client-facing share page + PDF exports.
// English is the default everywhere (the internal editing page never mounts a
// provider, so it stays EN). The share page wraps its report tree in
// <I18nProvider lang="he"> to switch to Hebrew (RTL).
//
// Numbers, currency, percentages and dates are formatted by the language-agnostic
// helpers in analysisEngine (aedStr/ilsStr/pct) and stay LTR — in RTL they are
// isolated via CSS ([dir="rtl"] .tabular-nums { direction:ltr; unicode-bidi:isolate }).
// ==========================================================================
import { createContext, useContext } from 'react';

export const DICT = {
  en: {
    // ---- ShareView header / chrome ----
    investment_analysis: 'Investment Analysis',
    prepared_by_x: 'Prepared by {name} · {company}',
    overview: 'Overview',
    full_detail: 'Full detail',
    the_essentials: 'the essentials',
    full_tables: 'full tables, charts & sensitivity',
    download_pdf: '⬇ Download PDF',
    generating: 'Generating…',
    pdf_desktop_a4: 'PDF — Desktop (A4)',
    pdf_desktop_a4_desc: 'Full print-ready report',
    pdf_mobile: 'PDF — Mobile',
    pdf_mobile_desc: 'Single column, phone-friendly',
    discuss_on_whatsapp: 'Discuss on WhatsApp',
    interested: 'Interested?',
    talk_to_x: 'Talk to {name}',
    link_expired: 'This link has expired.',
    link_unavailable: 'This link is no longer available.',
    contact_for_link: 'Please contact {name} for an updated link.',

    // ---- section titles ----
    executive_summary: 'Executive Summary',
    auto_generated: 'auto-generated',
    payment_schedule: 'Payment Schedule',
    return_on_investment: 'Return on Investment',
    exit_scenario: 'Exit Scenario',
    cash_flow: 'Cash Flow',
    sensitivity_analysis: 'Sensitivity Analysis',
    price_per_sqft: 'Price per sqft',
    assumptions_fees_used: 'Assumptions & fees used',
    str_expense_breakdown: 'STR Expense Breakdown',
    ltr_vs_str: 'Long-term vs Short-term',

    // ---- hero ----
    handover: 'Handover',
    handover_kw: '🔑 Handover {date}',
    unit_kw: 'Unit {n}',
    below_market: '{x} below market',
    off_plan: 'Off-Plan',
    dubai: 'Dubai',
    rak: 'Ras Al Khaimah',
    short_term_rental: 'Short-term rental',
    ltr_vs_str_badge: 'LTR vs STR',

    // ---- key numbers strip ----
    until_handover: 'Until handover',
    on_handover: 'On handover',
    paid_during_construction: 'paid during construction',
    final_milestone: 'final milestone',
    incl_furnishing: 'incl. furnishing',
    str_net_income_yr: 'STR net income / yr',
    expected_rent_yr: 'Expected rent / yr',
    gross_yield_x: 'gross yield {x}',
    gross_x: 'gross {x}',
    net_rental_yield: 'Net rental yield',
    str_net_yield: 'STR net yield',
    after_service_charge: 'after service charge',
    roi_at_maturity_y: 'ROI at maturity (+{y}y)',
    capital_on_cash: 'capital, on cash deployed',
    incl_fees_x: 'incl. {x} fees ({labels})',

    // ---- discount / instant equity hero ----
    instant_equity_below: 'Instant equity — buying below market',
    day_one_gain: 'Day-one gain: the gap between market value and your price.',
    below: 'below',
    your_price: 'Your price',
    market_value: 'Market value',
    instant_equity_day1: 'Instant equity (day 1)',
    instant_equity_below_short: '⚡ Instant equity — below market',

    // ---- ROI headline cards ----
    capital_roi_handover: 'Capital ROI @ Handover',
    keep_unit_ho: 'if you keep the unit — handover payment included',
    flip_handover_roi: 'Flip @ Handover ROI',
    resell_at_handover: 'if you resell at handover — before the handover payment',
    incl_resale_costs: ' · incl. resale costs',
    capital_roi_plus_y: 'Capital ROI @ +{y}yr',
    on_cash_deployed_x: 'on cash deployed · {x}',
    maturity: 'maturity',
    net_rental_yield_card: 'Net Rental Yield',
    gross_x_coc_y: 'gross {x} · cash-on-cash {y}',

    // ---- price per sqft ----
    per_sqft_suffix: ' /sqft',
    on_total_area: ' · on total area',
    total_area: ' · total area',
    x_total_sqft: '{x} total sqft',
    incl_balcony: ' incl. balcony',
    x_internal_y_balcony: '{i} internal + {b} balcony',
    market_x_sqft: 'market {x}/sqft',

    // ---- payment schedule table ----
    milestone: 'Milestone',
    pct_col: '%',
    amount: 'Amount',
    due: 'Due',
    cumulative: 'Cumulative',
    cum_pct: 'Cum %',
    today: 'Today',
    fee: 'fee',
    x_paid: '{x} paid',
    cum_x: 'cum {x}',
    post_handover_phpp: 'Post-handover (PHPP)',
    phpp_installments_total: 'Total PHPP installments due',
    net_by_phpp_end: 'Total paid by PHPP end (net of rent)',
    net_by_phpp_end_caption: 'After {rent} rent collected through {year}',
    grand_total_incl_fees: 'Grand total incl. fees',

    // ---- ROI table ----
    roi_cash_note: 'Capital & Combined ROI are cash-on-cash — gross gain over cash deployed by each horizon (holding basis).',
    horizon: 'Horizon',
    property_value: 'Property Value',
    cash_deployed: 'Cash Deployed',
    capital_roi: 'Capital ROI',
    cum_net_rent: 'Cum. Net Rent',
    combined_roi: 'Combined ROI',
    at_handover: 'At Handover',
    plus_y_midpoint: '+{y} Years (mid-point)',
    plus_y_maturity: '+{y} Years (at maturity)',
    gross_yield_rp: 'Gross yield (rent/price)',
    net_yield_nc: 'Net yield (net/cost)',
    coc_yield: 'Cash-on-cash yield',
    coc_caption: 'year 1 — rent on cash deployed at handover',
    coc_hint: 'Sits above the net yield early on and converges toward it once every payment is in.',
    combined_roi_ann: 'Combined ROI (ann.)',
    break_even: 'Break-even',
    yr_x: 'Yr {x}',
    gt_x_yr: '>{x}yr',
    net_oop_title: 'Net out-of-pocket after rent collected',
    net_oop_note: ' — informational; cash deployed + service charges paid − gross rent banked. Not used in any ROI.',
    plus_yy: '+{y}y',
    appr_breakdown: 'Appreciation ROI @ maturity breaks down into:',
    instant_equity_discount: 'Instant equity (discount)',
    x_of_total_cost: '{x} of total cost',
    market_appreciation: 'Market appreciation',

    // ---- charts ----
    cumulative_payments: 'Cumulative Payments',
    money_in_over_time: 'Money in over time — builds to 100%',
    cumulative_paid: 'Cumulative paid',
    property_value_vs_invested: 'Property Value vs Total Invested',
    property_value_series: 'Property value',
    total_invested: 'Total invested',
    annual_gains: 'Annual Gains: Net Rent + Appreciation',
    net_rent: 'Net rent',
    appreciation_cum: 'Appreciation (cum.)',
    combined_roi_over_time: 'Combined ROI % Over Time',
    break_even_year_x: 'Break-even in year {x} · ',
    on_cash_each_point: 'on cash deployed at each point',
    combined_roi_series: 'Combined ROI',
    no_data_to_plot: 'No data to plot',

    // ---- exit scenario card ----
    sell_x: 'sell {date}',
    resale_simulator: 'Resale simulator — profit & ROI on cash deployed if you sell {hold}.',
    at_handover_lc: 'at handover',
    x_years_after_handover: '{n} year(s) after handover',
    exit_after: 'Exit after',
    at_handover_cap: 'At handover',
    plus_n_yr: '+{n} yr',
    plus_x_yr: '+{x}yr',
    include_resale_costs: 'Include resale costs',
    resale_costs_note: 'Broker commission + NOC fee, deducted from proceeds',
    broker_commission: 'Broker commission',
    noc_fee: 'NOC fee',
    sale_value_at_exit: 'Sale value at exit {market}',
    market_paren: '(market)',
    less_broker_commission_x: 'Less: broker commission ({x})',
    less_noc_fee: 'Less: NOC fee',
    less_total_purchase: 'Less: total purchase price',
    less_purchase_price: 'Less: purchase price',
    x_installments_owed: '({n} installment(s) still owed)',
    plus_net_rent_collected: 'Plus: net rent collected',
    x_yr_holding: '({n} yr holding)',
    exit_profit: 'Exit profit',
    capital_gain_plus_rent: '(capital gain + rent)',
    net_after_fees_sc: 'Net after fees & service charges',
    cash_invested_pf: 'Cash invested — payments + fees',
    roi_on_cash: 'ROI on cash',
    annualized_roi: 'Annualized ROI',
    resale_incl_note: 'Includes {c} broker commission and {n} NOC fee.',
    resale_excl_note: 'Excludes resale transfer/NOC fees and agent commission.',
    exit_profit_roi_sell: 'Profit & ROI if you sell {hold}.',
    break_even_in_year_x: 'Break-even in year {x}',
    cum_appr_vs_cost: 'Cumulative appreciation + net rent vs cost',

    // ---- assumptions ----
    fees: 'Fees',
    projection_assumptions: 'Projection assumptions',
    dld_at_handover: 'DLD ({r}%) — at handover',
    dld_at_20: 'DLD ({r}%) — at 20% milestone',
    paid_by_developer: 'Paid by developer',
    admin_fee_at_booking: 'Admin fee (at booking)',
    service_charge_from_ho: 'Service charge (from handover)',
    per_yr: '/yr',
    cap_appr_bands: 'Capital appreciation %/yr (1–3 / 4–6 / 7–10)',
    rent_appr_bands: 'Rent appreciation %/yr',
    starting_rent_yearly: 'Starting rent (yearly)',
    projection_horizon: 'Projection horizon',
    x_years_from_handover: '{x} years from handover',
    market_value_base: 'Market value (appreciation base)',

    // ---- cash flow ----
    quarterly: 'Quarterly',
    yearly: 'Yearly',
    monthly: 'Monthly',
    peak_cash_requirement: 'Peak cash requirement',
    peak_hint: 'the real "how much money do I need"',
    on_x: 'on {x}',
    cash_break_even: 'Cash break-even',
    be_hint: 'running cash position crosses zero',
    cumulative_turns_positive: 'cumulative turns positive',
    not_within_horizon: 'not within the horizon',
    cumulative_position_plus_y: 'Cumulative position at +{y}y',
    horizon_end_hint: 'end of the projection horizon',
    cash_positive: 'cash-positive',
    still_recovering: 'still recovering cash',
    beyond_plus_y: 'beyond +{y}y',
    cf_note: 'Money out (milestone payments incl. fees & PHPP, plus ownership costs from handover) vs money in ({rev}). The cumulative line is your real out-of-pocket position — it crosses zero at cash break-even. {alt}',
    gross_str_revenue: 'gross STR revenue',
    rent_lc: 'rent',
    cf_alt_note: 'Dashed line = net cash flow under the other rental strategy.',
    period: 'Period',
    payments: 'Payments',
    ownership: 'Ownership',
    rent_in: 'Rent in',
    net: 'Net',
    money_in: 'Money in',
    money_out: 'Money out',
    money_in_rent: 'Money in (rent)',
    net_cash_flow: 'Net cash flow',
    cumulative_position: 'Cumulative position',
    net_other_strategy: 'Net — other strategy',
    x_total: '{x} total',
    cash_be_x: 'Cash break-even {x}',
    cf_not_reached: 'Cash break-even is not reached within the {y}-year horizon — the cumulative position ends at {x}. This is cash-only; the property’s capital value (see the Exit Scenario) is realised on sale, not in this table.',
    cf_mobile_note: 'Yearly money in vs out and your cumulative cash position.',
    end_position_plus_y: 'End position (+{y}y)',
    gt_plus_y: '>+{y}y',

    // ---- sensitivity ----
    sensitivity_note: 'How the outcome moves with rent and appreciation. The base case (current assumptions) is outlined.',
    exit_profit_at_x: 'Exit profit at {x} — rent × year 1–3 appreciation',
    revenue: 'Revenue',
    rent: 'Rent',
    appr_short: 'Appr.',
    base: 'Base',
    sens_note_a: 'Exit profit = sale value − purchase price + net rent{resale}; columns set the year 1–3 appreciation band, rows scale expected {rev} ±20%.',
    minus_resale_costs: ' − resale costs',
    ann_combined_roi_title: 'Annualized combined ROI — rent × exit year',
    exit_col: 'Exit',
    sens_note_b: 'Annualized combined ROI = (1 + profit ÷ cash invested)^(1 ÷ years held) − 1; columns set the exit year, rows scale expected {rev} ±20%.',
    exit_handover: 'Handover',

    // ---- STR ----
    gross_revenue_yr: 'Gross revenue / yr',
    x_occ: '{x}% occ',
    total_expenses_yr: 'Total expenses / yr',
    x_of_gross: '{x} of gross',
    net_income_yr: 'Net income / yr',
    net_yield: 'Net yield',
    on_total_cost_furnishing: 'on total cost incl. furnishing',
    payback_vs_lt: 'Payback vs long-term',
    x_yr: '{x} yr',
    to_recoup_furnishing: 'to recoup furnishing',
    str_le_ltr_net: 'STR ≤ LTR net',
    str_year1_line: 'Year 1 · {occ}% occupancy · {nights} occupied nights @ ADR {adr}/night',
    line_item: 'Line item',
    month_col: 'Monthly',
    year_col: 'Yearly',
    ils_yr: 'ILS/yr',
    pct_of_gross: '% of gross',
    gross_rental_revenue: 'Gross rental revenue',
    total_expenses: 'Total expenses',
    net_income: 'Net income',
    scales: 'scales',
    str_nets_more: 'Short-term nets {x} more per year',
    ltr_nets_more: 'Long-term nets {x} more per year',
    yield_in_str_favour: 'in STR favour',
    yield_in_ltr_favour: 'in LTR favour',
    x_net_yield: '{x} net yield {favour}',
    breakeven_occ_line: 'Break-even occupancy: STR net equals LTR net at {occ} occupancy (you’re assuming {assume}).',
    metric: 'Metric',
    long_term: 'Long-term',
    short_term: 'Short-term',
    gross_income_yr: 'Gross income / yr',
    net_income_over_horizon: 'Net income over the projection horizon',
    long_term_net: 'Long-term net',
    short_term_net: 'Short-term net',

    // ---- STR expense line items (engine keys) ----
    str_li_management: 'Management company',
    str_li_dewa: 'DEWA (water & electricity)',
    str_li_gas: 'Gas',
    str_li_internet: 'Internet',
    str_li_chiller: 'Chiller / cooling',
    str_li_dtcm: 'DTCM holiday-home license',
    str_li_sc: 'Service charge',
    str_li_misc: 'Other / miscellaneous',

    // ---- milestone / fee labels (known values; custom labels pass through) ----
    ms_booking: 'Booking',
    ms_handover: 'Handover',
    ms_down_payment: 'Down Payment',
    ms_on_booking: 'On Booking',
    ms_admin_fee: 'Admin Fee',
    ms_dld_handover: 'DLD 4% (handover)',
    ms_dld_20: 'DLD 4% (20% milestone)',
    ms_furnishing_str: 'Furnishing (STR)',
    ms_furnishing_onetime: 'Furnishing (one-time)',
    ms_service_charge: 'Service charge',
    ms_str_revenue: 'STR revenue',
    ms_rent: 'Rent',
    ms_str_opex: 'STR operating costs',
    badge_handover: 'handover',

    // ---- footer ----
    prepared_by: 'Prepared by',
    footer_disclaimer: 'Projections are estimates based on the stated assumptions and are not guaranteed.',
    footer_rate_fixed: ' AED/ILS rate used: {x} (fixed).',
    footer_rate_indicative: ' All figures in AED with indicative ILS conversion.',
    disclaimer_short: 'Projections are estimates based on stated assumptions and are not guaranteed.',

    // ---- bulk ----
    bulk_deal: 'Bulk Deal',
    package_purchase: 'Package Purchase',
    units_in_package: 'Units in this package',
    package_price: 'Package price',
    package_total: 'Package total',
    net_rent_yr: 'Net rent / yr',

    // ---- exec summary bullet labels ----
    es_capital_requirements: 'Capital Requirements',
    es_growth_velocity: 'Growth Velocity',
    es_cash_flow: 'Cash Flow',
    es_capital_at_work: 'Capital at Work',
    es_safety_margin: 'Safety Margin',
    es_tenant_funded: 'Tenant-funded plan',

    // ---- bulk report + comparison additions ----
    per_unit_suffix: ' / unit',
    per_unit_equivalent: 'per-unit equivalent',
    per_u_suffix: '/u',
    n_units: '{n} units',
    units_col: 'Units',
    unit_col: 'Unit',
    qty_col: 'Qty',
    area_col: 'Area',
    list_price_col: 'List price',
    negotiated_price_col: 'Negotiated price',
    rent_yr_col: 'Rent / yr',
    instant_equity_col: 'Instant equity',
    line_total_col: 'Line total',
    negotiated: 'negotiated',
    vs_x: 'vs {x}',
    below_list: '{x} below list',
    total_package_area: 'Total package {area}',
    blended_psf_psm: 'blended {psf}/sqft · {psm}',
    blended_price_sqft: 'Blended price / sqft',
    psf_psm: '{psf}/sqft · {psm}',
    total_lc: 'total',
    net_yield_x: 'net yield {x}',
    gross_yield: 'Gross yield',
    instant_equity_below_ref: 'Instant equity — buying below {ref}',
    ref_market_list: 'market / list',
    ref_market_value: 'market value',
    ref_list_price: 'list price',
    aggregate_day_one: 'Aggregate day-one gain: reference {ref} minus negotiated price, across all units.',
    reference_value_x: 'Reference value ({ref})',
    combined_payment_schedule: 'Combined Payment Schedule',
    all_units_merged: 'all units merged chronologically',
    aggregate_cash_flow: 'Aggregate Cash Flow',
    cf_ledger_note: "Dated ledger of every unit's payments, fees, rent and service charge — merged.",
    gt_x_y: '>{x}y',
    cum_pos_turns_positive: 'cumulative position turns positive',
    net_position_plus_y: 'Net position at +{y}y',
    rent_in_cash_out: 'rent in − all cash out',
    cumulative_cash_position: 'Cumulative cash position',
    return_on_investment_package: 'Return on Investment — package',
    roi_package_note: 'Cash-on-cash: aggregate gross gain over aggregate cash deployed. Per-unit equivalent shown beneath each aggregate.',
    package_value_vs_invested: 'Package Value vs Total Invested',
    package_value_series: 'Package value',
    no_data: 'No data',
    exit_year_aria: 'Exit year',
    exit_scenario_package: 'Exit Scenario — whole package',
    sell_all_units: 'Sell all {units} units {hold}. Profit & ROI on cash deployed.',
    resale_costs_per_unit_note: '2% commission per unit sale + NOC fee × {units} units',
    broker_commission_per_unit: 'Broker commission (per unit)',
    noc_fee_per_unit: 'NOC fee (per unit)',
    sale_value_at_exit_units: 'Sale value at exit ({units} units)',
    less_noc_fee_x: 'Less: NOC fee ({x})',
    per_unit_profit_equiv: 'Per-unit profit equivalent',
    resale_incl_note_bulk: 'Includes {c} broker commission on each of {units} sales and {n} NOC fee per unit.',
    // comparison rows / labels
    cmp_price: 'Price',
    cmp_internal_area: 'Internal area',
    cmp_price_per_sqft: 'Price / sqft',
    cmp_discount_market: 'Discount to market',
    cmp_paid_until_handover: 'Paid until handover',
    cmp_total_incl_fees: 'Total incl. fees',
    cmp_dld_admin_fees: 'DLD + admin fees',
    cmp_service_charge_yr: 'Service charge / yr',
    cmp_value_handover: 'Value @ handover',
    cmp_value_midpoint: 'Value @ mid-point',
    cmp_value_maturity: 'Value @ maturity',
    cmp_capital_roi_handover: 'Capital ROI @ Handover (hold)',
    cmp_flip_handover: 'Flip @ Handover (sell)',
    cmp_capital_roi_maturity: 'Capital ROI @ maturity (on cash)',
    cmp_combined_roi_ann: 'Combined ROI (annualized)',
    cmp_break_even_year: 'Break-even year',
    cmp_exit_sale_value: 'Exit: sale value',
    cmp_exit_outstanding: 'Exit: outstanding to developer',
    cmp_exit_payments_fees: 'Exit: payments + fees',
    cmp_exit_profit: 'Exit: profit (gain + rent)',
    cmp_exit_net_after: 'Exit: net after fees & SC',
    cmp_exit_roi: 'Exit: ROI on cash',
    cmp_exit_ann_roi: 'Exit: annualized ROI',
    cmp_side_by_side: 'Side-by-Side Comparison',
    cmp_applied_all_units: '(applied to all units)',
    phpp_title: 'Post-Handover Payment Plan — pay after receiving the keys',
    cmp_best_value_note: '🟢 Best value in each row is highlighted automatically.',
    cmp_value_projection: 'Property Value Projection',
    cmp_str_adr: 'STR ADR (AED/night)',
    cmp_str_occupancy: 'STR occupancy',
    cmp_no_str_data: 'No STR data',
    cmp_no_str_data_title: 'No ADR set for this unit — set one on its own assumptions panel for a meaningful STR comparison.',
    cmp_net_income_over_time: 'Net Income Over Time',
    ltr_short: 'LTR',
    str_short: 'STR',
  },

  he: {
    // ---- ShareView header / chrome ----
    investment_analysis: 'ניתוח השקעה',
    prepared_by_x: 'הוכן על ידי {name} · {company}',
    overview: 'סקירה',
    full_detail: 'פירוט מלא',
    the_essentials: 'העיקר',
    full_tables: 'טבלאות, גרפים וניתוח רגישות מלאים',
    download_pdf: '⬇ הורדת PDF',
    generating: 'מפיק…',
    pdf_desktop_a4: 'PDF — מחשב (A4)',
    pdf_desktop_a4_desc: 'דוח מלא מוכן להדפסה',
    pdf_mobile: 'PDF — נייד',
    pdf_mobile_desc: 'עמודה אחת, מותאם לנייד',
    discuss_on_whatsapp: 'שיחה בוואטסאפ',
    interested: 'מעוניינים?',
    talk_to_x: 'דברו עם {name}',
    link_expired: 'תוקף הקישור פג.',
    link_unavailable: 'הקישור אינו זמין עוד.',
    contact_for_link: 'אנא צרו קשר עם {name} לקישור מעודכן.',

    // ---- section titles ----
    executive_summary: 'תקציר מנהלים',
    auto_generated: 'מופק אוטומטית',
    payment_schedule: 'לוח תשלומים',
    return_on_investment: 'תשואה על ההשקעה',
    exit_scenario: 'תרחיש יציאה',
    cash_flow: 'תזרים מזומנים',
    sensitivity_analysis: 'ניתוח רגישות',
    price_per_sqft: 'מחיר לרגל רבועה',
    assumptions_fees_used: 'הנחות ועמלות בשימוש',
    str_expense_breakdown: 'פירוט הוצאות השכרה קצרת טווח',
    ltr_vs_str: 'טווח ארוך מול טווח קצר',

    // ---- hero ----
    handover: 'מסירה',
    handover_kw: '🔑 מסירה {date}',
    unit_kw: 'יחידה {n}',
    below_market: '{x} מתחת לשוק',
    off_plan: 'אוף-פלאן',
    dubai: 'דובאי',
    rak: 'ראס אל-חיימה',
    short_term_rental: 'השכרה קצרת טווח',
    ltr_vs_str_badge: 'טווח ארוך מול קצר',

    // ---- key numbers strip ----
    until_handover: 'עד המסירה',
    on_handover: 'במסירה',
    paid_during_construction: 'משולם במהלך הבנייה',
    final_milestone: 'אבן הדרך האחרונה',
    incl_furnishing: 'כולל ריהוט',
    str_net_income_yr: 'הכנסה נטו מהשכרה קצרת טווח / שנה',
    expected_rent_yr: 'שכר דירה צפוי / שנה',
    gross_yield_x: 'תשואה ברוטו {x}',
    gross_x: 'ברוטו {x}',
    net_rental_yield: 'תשואת שכירות נטו',
    str_net_yield: 'תשואת נטו – השכרה קצרת טווח',
    after_service_charge: 'לאחר דמי ניהול',
    roi_at_maturity_y: 'תשואה בבשלות (+{y} ש׳)',
    capital_on_cash: 'הון, על המזומן שהושקע',
    incl_fees_x: 'כולל {x} עמלות ({labels})',

    // ---- discount / instant equity hero ----
    instant_equity_below: 'הון מיידי — רכישה מתחת למחיר השוק',
    day_one_gain: 'רווח מהיום הראשון: הפער בין שווי השוק למחיר שלכם.',
    below: 'מתחת',
    your_price: 'המחיר שלכם',
    market_value: 'שווי שוק',
    instant_equity_day1: 'הון מיידי (יום 1)',
    instant_equity_below_short: '⚡ הון מיידי — מתחת לשוק',

    // ---- ROI headline cards ----
    capital_roi_handover: 'תשואת הון במסירה',
    keep_unit_ho: 'אם שומרים על היחידה — כולל תשלום המסירה',
    flip_handover_roi: 'תשואת מכירה במסירה',
    resell_at_handover: 'אם מוכרים במסירה — לפני תשלום המסירה',
    incl_resale_costs: ' · כולל עלויות מכירה',
    capital_roi_plus_y: 'תשואת הון ב-+{y} ש׳',
    on_cash_deployed_x: 'על המזומן שהושקע · {x}',
    maturity: 'בשלות',
    net_rental_yield_card: 'תשואת שכירות נטו',
    gross_x_coc_y: 'ברוטו {x} · תשואה על מזומן {y}',

    // ---- price per sqft ----
    per_sqft_suffix: ' / רגל²',
    on_total_area: ' · על השטח הכולל',
    total_area: ' · שטח כולל',
    x_total_sqft: '{x} רגל² סה״כ',
    incl_balcony: ' כולל מרפסת',
    x_internal_y_balcony: '{i} פנימי + {b} מרפסת',
    market_x_sqft: 'שוק {x}/רגל²',

    // ---- payment schedule table ----
    milestone: 'אבן דרך',
    pct_col: '%',
    amount: 'סכום',
    due: 'מועד',
    cumulative: 'מצטבר',
    cum_pct: '% מצטבר',
    today: 'היום',
    fee: 'עמלה',
    x_paid: '{x} שולם',
    cum_x: 'מצטבר {x}',
    post_handover_phpp: 'לאחר מסירה (PHPP)',
    phpp_installments_total: 'סה"כ תשלומי PHPP',
    net_by_phpp_end: 'סה"כ שולם עד תום ה-PHPP (נטו שכירות)',
    net_by_phpp_end_caption: 'לאחר קיזוז {rent} דמי שכירות שנגבו עד {year}',
    grand_total_incl_fees: 'סך הכל כולל עמלות',

    // ---- ROI table ----
    roi_cash_note: 'תשואת הון ותשואה משולבת הן על בסיס מזומן — רווח ברוטו ביחס למזומן שהושקע עד כל אופק (בהחזקה).',
    horizon: 'אופק',
    property_value: 'שווי הנכס',
    cash_deployed: 'מזומן שהושקע',
    capital_roi: 'תשואת הון',
    cum_net_rent: 'שכ״ד נטו מצטבר',
    combined_roi: 'תשואה משולבת',
    at_handover: 'במסירה',
    plus_y_midpoint: '+{y} שנים (אמצע)',
    plus_y_maturity: '+{y} שנים (בבשלות)',
    gross_yield_rp: 'תשואה ברוטו (שכ״ד/מחיר)',
    net_yield_nc: 'תשואה נטו (נטו/עלות)',
    coc_yield: 'תשואה על מזומן',
    coc_caption: 'שנה 1 — שכ״ד על המזומן שהושקע במסירה',
    coc_hint: 'גבוהה מהתשואה נטו בהתחלה ומתכנסת אליה כשכל התשלומים בוצעו.',
    combined_roi_ann: 'תשואה משולבת (שנתית)',
    break_even: 'נקודת איזון',
    yr_x: 'שנה {x}',
    gt_x_yr: '>{x} ש׳',
    net_oop_title: 'הוצאה נטו מהכיס לאחר גביית שכ״ד',
    net_oop_note: ' — אינפורמטיבי; מזומן שהושקע + דמי ניהול ששולמו − שכ״ד ברוטו שנגבה. אינו נכלל בתשואה.',
    plus_yy: '+{y} ש׳',
    appr_breakdown: 'תשואת עליית הערך בבשלות מתחלקת ל:',
    instant_equity_discount: 'הון מיידי (הנחה)',
    x_of_total_cost: '{x} מהעלות הכוללת',
    market_appreciation: 'עליית ערך שוק',

    // ---- charts ----
    cumulative_payments: 'תשלומים מצטברים',
    money_in_over_time: 'כסף שנכנס לאורך זמן — עד 100%',
    cumulative_paid: 'שולם מצטבר',
    property_value_vs_invested: 'שווי הנכס מול סך ההשקעה',
    property_value_series: 'שווי הנכס',
    total_invested: 'סך ההשקעה',
    annual_gains: 'רווחים שנתיים: שכ״ד נטו + עליית ערך',
    net_rent: 'שכ״ד נטו',
    appreciation_cum: 'עליית ערך (מצטבר)',
    combined_roi_over_time: 'תשואה משולבת % לאורך זמן',
    break_even_year_x: 'נקודת איזון בשנה {x} · ',
    on_cash_each_point: 'על המזומן שהושקע בכל נקודה',
    combined_roi_series: 'תשואה משולבת',
    no_data_to_plot: 'אין נתונים להצגה',

    // ---- exit scenario card ----
    sell_x: 'מכירה {date}',
    resale_simulator: 'סימולטור מכירה — רווח ותשואה על המזומן אם תמכרו {hold}.',
    at_handover_lc: 'במסירה',
    x_years_after_handover: '{n} שנים לאחר המסירה',
    exit_after: 'יציאה לאחר',
    at_handover_cap: 'במסירה',
    plus_n_yr: '+{n} ש׳',
    plus_x_yr: '+{x} ש׳',
    include_resale_costs: 'כלול עלויות מכירה',
    resale_costs_note: 'עמלת תיווך + אגרת NOC, מנוכות מהתמורה',
    broker_commission: 'עמלת תיווך',
    noc_fee: 'אגרת NOC',
    sale_value_at_exit: 'שווי מכירה ביציאה {market}',
    market_paren: '(שוק)',
    less_broker_commission_x: 'בניכוי: עמלת תיווך ({x})',
    less_noc_fee: 'בניכוי: אגרת NOC',
    less_total_purchase: 'בניכוי: מחיר רכישה כולל',
    less_purchase_price: 'בניכוי: מחיר רכישה',
    x_installments_owed: '(עדיין חייבים {n} תשלומים)',
    plus_net_rent_collected: 'בתוספת: שכ״ד נטו שנגבה',
    x_yr_holding: '(החזקה {n} שנים)',
    exit_profit: 'רווח ביציאה',
    capital_gain_plus_rent: '(רווח הון + שכ״ד)',
    net_after_fees_sc: 'נטו לאחר עמלות ודמי ניהול',
    cash_invested_pf: 'מזומן שהושקע — תשלומים + עמלות',
    roi_on_cash: 'תשואה על מזומן',
    annualized_roi: 'תשואה שנתית',
    resale_incl_note: 'כולל עמלת תיווך של {c} ואגרת NOC של {n}.',
    resale_excl_note: 'לא כולל אגרות העברה/NOC ועמלת תיווך במכירה.',
    exit_profit_roi_sell: 'רווח ותשואה אם תמכרו {hold}.',
    break_even_in_year_x: 'נקודת איזון בשנה {x}',
    cum_appr_vs_cost: 'עליית ערך מצטברת + שכ״ד נטו מול עלות',

    // ---- assumptions ----
    fees: 'עמלות',
    projection_assumptions: 'הנחות התחזית',
    dld_at_handover: 'DLD ({r}%) — במסירה',
    dld_at_20: 'DLD ({r}%) — באבן הדרך של 20%',
    paid_by_developer: 'משולם על ידי היזם',
    admin_fee_at_booking: 'דמי ניהול (בהזמנה)',
    service_charge_from_ho: 'דמי ניהול (מהמסירה)',
    per_yr: '/שנה',
    cap_appr_bands: 'עליית ערך הון %/שנה (1–3 / 4–6 / 7–10)',
    rent_appr_bands: 'עליית שכ״ד %/שנה',
    starting_rent_yearly: 'שכ״ד התחלתי (שנתי)',
    projection_horizon: 'אופק התחזית',
    x_years_from_handover: '{x} שנים מהמסירה',
    market_value_base: 'שווי שוק (בסיס עליית הערך)',

    // ---- cash flow ----
    quarterly: 'רבעוני',
    yearly: 'שנתי',
    monthly: 'חודשי',
    peak_cash_requirement: 'דרישת מזומן שיא',
    peak_hint: 'ה"כמה כסף אני צריך" האמיתי',
    on_x: 'ב-{x}',
    cash_break_even: 'נקודת איזון מזומן',
    be_hint: 'מצב המזומן חוצה את האפס',
    cumulative_turns_positive: 'המצטבר הופך חיובי',
    not_within_horizon: 'לא בתוך האופק',
    cumulative_position_plus_y: 'מצב מצטבר ב-+{y} ש׳',
    horizon_end_hint: 'סוף אופק התחזית',
    cash_positive: 'חיובי במזומן',
    still_recovering: 'עדיין מחזיר מזומן',
    beyond_plus_y: 'מעבר ל-+{y} ש׳',
    cf_note: 'כסף יוצא (תשלומי אבני דרך כולל עמלות ו-PHPP, ועלויות בעלות מהמסירה) מול כסף נכנס ({rev}). הקו המצטבר הוא מצב הכיס האמיתי שלכם — הוא חוצה אפס בנקודת איזון המזומן. {alt}',
    gross_str_revenue: 'הכנסות ברוטו מהשכרה קצרת טווח',
    rent_lc: 'שכר דירה',
    cf_alt_note: 'קו מקווקו = תזרים נטו באסטרטגיית ההשכרה האחרת.',
    period: 'תקופה',
    payments: 'תשלומים',
    ownership: 'בעלות',
    rent_in: 'שכ״ד נכנס',
    net: 'נטו',
    money_in: 'כסף נכנס',
    money_out: 'כסף יוצא',
    money_in_rent: 'כסף נכנס (שכ״ד)',
    net_cash_flow: 'תזרים נטו',
    cumulative_position: 'מצב מצטבר',
    net_other_strategy: 'נטו — אסטרטגיה אחרת',
    x_total: 'סה״כ {x}',
    cash_be_x: 'נקודת איזון מזומן {x}',
    cf_not_reached: 'נקודת איזון המזומן אינה מושגת בתוך אופק {y} השנים — המצב המצטבר מסתיים ב-{x}. זהו מזומן בלבד; ערך ההון של הנכס (ראו תרחיש יציאה) מתממש במכירה, לא בטבלה זו.',
    cf_mobile_note: 'כסף שנתי נכנס מול יוצא ומצב המזומן המצטבר שלכם.',
    end_position_plus_y: 'מצב בסיום (+{y} ש׳)',
    gt_plus_y: '>+{y} ש׳',

    // ---- sensitivity ----
    sensitivity_note: 'כיצד התוצאה משתנה עם שכר הדירה ועליית הערך. מקרה הבסיס (ההנחות הנוכחיות) מסומן במסגרת.',
    exit_profit_at_x: 'רווח ביציאה ב-{x} — שכ״ד × עליית ערך שנים 1–3',
    revenue: 'הכנסות',
    rent: 'שכ״ד',
    appr_short: 'עליית ערך',
    base: 'בסיס',
    sens_note_a: 'רווח ביציאה = שווי מכירה − מחיר רכישה + שכ״ד נטו{resale}; העמודות קובעות את שיעור עליית הערך לשנים 1–3, השורות משנות את {rev} הצפוי ב-±20%.',
    minus_resale_costs: ' − עלויות מכירה',
    ann_combined_roi_title: 'תשואה משולבת שנתית — שכ״ד × שנת יציאה',
    exit_col: 'יציאה',
    sens_note_b: 'תשואה משולבת שנתית = (1 + רווח ÷ מזומן שהושקע)^(1 ÷ שנות החזקה) − 1; העמודות קובעות את שנת היציאה, השורות משנות את {rev} הצפוי ב-±20%.',
    exit_handover: 'מסירה',

    // ---- STR ----
    gross_revenue_yr: 'הכנסות ברוטו / שנה',
    x_occ: '{x}% תפוסה',
    total_expenses_yr: 'סך הוצאות / שנה',
    x_of_gross: '{x} מהברוטו',
    net_income_yr: 'הכנסה נטו / שנה',
    net_yield: 'תשואה נטו',
    on_total_cost_furnishing: 'על העלות הכוללת כולל ריהוט',
    payback_vs_lt: 'החזר מול טווח ארוך',
    x_yr: '{x} ש׳',
    to_recoup_furnishing: 'להחזר הריהוט',
    str_le_ltr_net: 'קצר ≤ ארוך נטו',
    str_year1_line: 'שנה 1 · {occ}% תפוסה · {nights} לילות מאוכלסים @ מחיר לילה {adr}',
    line_item: 'סעיף',
    month_col: 'חודשי',
    year_col: 'שנתי',
    ils_yr: '₪/שנה',
    pct_of_gross: '% מהברוטו',
    gross_rental_revenue: 'הכנסות שכירות ברוטו',
    total_expenses: 'סך הוצאות',
    net_income: 'הכנסה נטו',
    scales: 'משתנה',
    str_nets_more: 'טווח קצר מניב {x} יותר בשנה',
    ltr_nets_more: 'טווח ארוך מניב {x} יותר בשנה',
    yield_in_str_favour: 'לטובת טווח קצר',
    yield_in_ltr_favour: 'לטובת טווח ארוך',
    x_net_yield: '{x} תשואה נטו {favour}',
    breakeven_occ_line: 'תפוסת איזון: נטו קצר משתווה לנטו ארוך ב-{occ} תפוסה (ההנחה שלכם {assume}).',
    metric: 'מדד',
    long_term: 'טווח ארוך',
    short_term: 'טווח קצר',
    gross_income_yr: 'הכנסה ברוטו / שנה',
    net_income_over_horizon: 'הכנסה נטו לאורך אופק התחזית',
    long_term_net: 'נטו טווח ארוך',
    short_term_net: 'נטו טווח קצר',

    // ---- STR expense line items ----
    str_li_management: 'חברת ניהול',
    str_li_dewa: 'DEWA (מים וחשמל)',
    str_li_gas: 'גז',
    str_li_internet: 'אינטרנט',
    str_li_chiller: 'קירור',
    str_li_dtcm: 'רישיון דירת נופש DTCM',
    str_li_sc: 'דמי ניהול',
    str_li_misc: 'אחר / שונות',

    // ---- milestone / fee labels ----
    ms_booking: 'הזמנה',
    ms_handover: 'מסירה',
    ms_down_payment: 'מקדמה',
    ms_on_booking: 'בהזמנה',
    ms_admin_fee: 'דמי ניהול',
    ms_dld_handover: 'DLD 4% (מסירה)',
    ms_dld_20: 'DLD 4% (אבן דרך 20%)',
    ms_furnishing_str: 'ריהוט (השכרה קצרת טווח)',
    ms_furnishing_onetime: 'ריהוט (חד-פעמי)',
    ms_service_charge: 'דמי ניהול',
    ms_str_revenue: 'הכנסות השכרה קצרת טווח',
    ms_rent: 'שכר דירה',
    ms_str_opex: 'עלויות תפעול השכרה קצרת טווח',
    badge_handover: 'מסירה',

    // ---- footer ----
    prepared_by: 'הוכן על ידי',
    footer_disclaimer: 'התחזיות הן הערכות המבוססות על ההנחות שצוינו ואינן מובטחות.',
    footer_rate_fixed: ' שער AED/ILS בשימוש: {x} (קבוע).',
    footer_rate_indicative: ' כל הסכומים ב-AED עם המרה מוערכת ל-₪.',
    disclaimer_short: 'התחזיות הן הערכות המבוססות על ההנחות שצוינו ואינן מובטחות.',

    // ---- bulk ----
    bulk_deal: 'עסקת מכלול',
    package_purchase: 'רכישת מכלול',
    units_in_package: 'היחידות במכלול',
    package_price: 'מחיר המכלול',
    package_total: 'סך המכלול',
    net_rent_yr: 'שכ״ד נטו / שנה',

    // ---- exec summary bullet labels ----
    es_capital_requirements: 'דרישות הון',
    es_growth_velocity: 'קצב צמיחה',
    es_cash_flow: 'תזרים מזומנים',
    es_capital_at_work: 'הון בעבודה',
    es_safety_margin: 'שולי ביטחון',
    es_tenant_funded: 'תוכנית במימון השוכר',

    // ---- bulk report + comparison additions ----
    per_unit_suffix: ' / יחידה',
    per_unit_equivalent: 'שווי ערך ליחידה',
    per_u_suffix: '/יח׳',
    n_units: '{n} יחידות',
    units_col: 'יחידות',
    unit_col: 'יחידה',
    qty_col: 'כמות',
    area_col: 'שטח',
    list_price_col: 'מחיר מחירון',
    negotiated_price_col: 'מחיר מוסכם',
    rent_yr_col: 'שכ״ד / שנה',
    instant_equity_col: 'הון מיידי',
    line_total_col: 'סה״כ שורה',
    negotiated: 'מוסכם',
    vs_x: 'מול {x}',
    below_list: '{x} מתחת למחירון',
    total_package_area: 'שטח מכלול כולל {area}',
    blended_psf_psm: 'משוקלל {psf}/רגל² · {psm}',
    blended_price_sqft: 'מחיר משוקלל / רגל²',
    psf_psm: '{psf}/רגל² · {psm}',
    total_lc: 'סה״כ',
    net_yield_x: 'תשואה נטו {x}',
    gross_yield: 'תשואה ברוטו',
    instant_equity_below_ref: 'הון מיידי — רכישה מתחת ל{ref}',
    ref_market_list: 'שוק / מחירון',
    ref_market_value: 'שווי שוק',
    ref_list_price: 'מחיר מחירון',
    aggregate_day_one: 'רווח יום ראשון מצרפי: {ref} בניכוי המחיר המוסכם, על פני כל היחידות.',
    reference_value_x: 'ערך ייחוס ({ref})',
    combined_payment_schedule: 'לוח תשלומים משולב',
    all_units_merged: 'כל היחידות ממוזגות לפי סדר כרונולוגי',
    aggregate_cash_flow: 'תזרים מזומנים מצרפי',
    cf_ledger_note: 'יומן מתוארך של תשלומים, עמלות, שכ״ד ודמי ניהול של כל יחידה — ממוזג.',
    gt_x_y: '>{x} ש׳',
    cum_pos_turns_positive: 'המצב המצטבר הופך חיובי',
    net_position_plus_y: 'מצב נטו ב-+{y} ש׳',
    rent_in_cash_out: 'שכ״ד נכנס − כל המזומן יוצא',
    cumulative_cash_position: 'מצב מזומן מצטבר',
    return_on_investment_package: 'תשואה על ההשקעה — מכלול',
    roi_package_note: 'תשואה על מזומן: רווח ברוטו מצרפי ביחס למזומן מצרפי שהושקע. שווי ערך ליחידה מוצג מתחת לכל ערך מצרפי.',
    package_value_vs_invested: 'שווי המכלול מול סך ההשקעה',
    package_value_series: 'שווי המכלול',
    no_data: 'אין נתונים',
    exit_year_aria: 'שנת יציאה',
    exit_scenario_package: 'תרחיש יציאה — כל המכלול',
    sell_all_units: 'מכירת כל {units} היחידות {hold}. רווח ותשואה על המזומן שהושקע.',
    resale_costs_per_unit_note: '2% עמלה לכל מכירת יחידה + אגרת NOC × {units} יחידות',
    broker_commission_per_unit: 'עמלת תיווך (ליחידה)',
    noc_fee_per_unit: 'אגרת NOC (ליחידה)',
    sale_value_at_exit_units: 'שווי מכירה ביציאה ({units} יחידות)',
    less_noc_fee_x: 'בניכוי: אגרת NOC ({x})',
    per_unit_profit_equiv: 'שווי רווח ליחידה',
    resale_incl_note_bulk: 'כולל עמלת תיווך של {c} על כל אחת מ-{units} המכירות ואגרת NOC של {n} ליחידה.',
    // comparison rows / labels
    cmp_price: 'מחיר',
    cmp_internal_area: 'שטח פנימי',
    cmp_price_per_sqft: 'מחיר / רגל²',
    cmp_discount_market: 'הנחה מהשוק',
    cmp_paid_until_handover: 'שולם עד המסירה',
    cmp_total_incl_fees: 'סה״כ כולל עמלות',
    cmp_dld_admin_fees: 'DLD + דמי ניהול',
    cmp_service_charge_yr: 'דמי ניהול / שנה',
    cmp_value_handover: 'שווי במסירה',
    cmp_value_midpoint: 'שווי באמצע התקופה',
    cmp_value_maturity: 'שווי בבשלות',
    cmp_capital_roi_handover: 'תשואת הון במסירה (החזקה)',
    cmp_flip_handover: 'מכירה במסירה (Flip)',
    cmp_capital_roi_maturity: 'תשואת הון בבשלות (על מזומן)',
    cmp_combined_roi_ann: 'תשואה משולבת (שנתית)',
    cmp_break_even_year: 'שנת נקודת איזון',
    cmp_exit_sale_value: 'יציאה: שווי מכירה',
    cmp_exit_outstanding: 'יציאה: יתרה ליזם',
    cmp_exit_payments_fees: 'יציאה: תשלומים + עמלות',
    cmp_exit_profit: 'יציאה: רווח (רווח הון + שכ״ד)',
    cmp_exit_net_after: 'יציאה: נטו לאחר עמלות ודמי ניהול',
    cmp_exit_roi: 'יציאה: תשואה על מזומן',
    cmp_exit_ann_roi: 'יציאה: תשואה שנתית',
    cmp_side_by_side: 'השוואה זו לצד זו',
    cmp_applied_all_units: '(חל על כל היחידות)',
    phpp_title: 'תוכנית תשלום לאחר מסירה — תשלום לאחר קבלת המפתחות',
    cmp_best_value_note: '🟢 הערך הטוב ביותר בכל שורה מודגש אוטומטית.',
    cmp_value_projection: 'תחזית שווי הנכס',
    cmp_str_adr: 'מחיר לילה STR (AED)',
    cmp_str_occupancy: 'תפוסת STR',
    cmp_no_str_data: 'אין נתוני STR',
    cmp_no_str_data_title: 'לא הוגדר מחיר לילה ליחידה זו — הגדירו אחד בהנחות היחידה להשוואת STR משמעותית.',
    cmp_net_income_over_time: 'הכנסה נטו לאורך זמן',
    ltr_short: 'LTR',
    str_short: 'STR',
  },
};

// Known milestone/fee label values → dictionary key. Custom labels David types
// (anything not in this map) pass through untranslated, as required.
const MILESTONE_KEYS = {
  'booking': 'ms_booking',
  'handover': 'ms_handover',
  'down payment': 'ms_down_payment',
  'downpayment': 'ms_down_payment',
  'on booking': 'ms_on_booking',
  'admin fee': 'ms_admin_fee',
  'dld 4% (handover)': 'ms_dld_handover',
  'dld 4% (20% milestone)': 'ms_dld_20',
  'furnishing (str)': 'ms_furnishing_str',
  'furnishing (one-time)': 'ms_furnishing_onetime',
  'service charge': 'ms_service_charge',
  'str revenue': 'ms_str_revenue',
  'rent': 'ms_rent',
  'str operating costs': 'ms_str_opex',
};

function interpolate(str, vars) {
  if (!vars) return str;
  return str.replace(/\{(\w+)\}/g, (m, k) => (vars[k] != null ? vars[k] : m));
}

// Build a translate function for a language. Falls back en → key so a missing
// key is visible but never crashes.
export function makeT(lang) {
  const table = DICT[lang] || DICT.en;
  return (key, vars) => {
    const s = table[key] != null ? table[key] : (DICT.en[key] != null ? DICT.en[key] : key);
    return interpolate(s, vars);
  };
}

// Translate a milestone / fee label. Known values → localized; user-entered
// custom labels are returned verbatim (never machine-translated).
export function translateMilestone(label, lang) {
  if (!label) return label;
  const key = MILESTONE_KEYS[String(label).trim().toLowerCase()];
  if (!key) return label;                       // custom label → as typed
  return makeT(lang)(key);
}

// Month-year formatting. EN keeps "Jan 2027"; HE uses numeric MM/YYYY to avoid
// any Gregorian/Hebrew-calendar ambiguity (per spec: numeric to be safe).
export function fmtMonthYear(d, lang) {
  if (!d) return '—';
  const date = new Date(d);
  if (lang === 'he') {
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    return `${mm}/${date.getFullYear()}`;
  }
  return date.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
}

// Default context is a complete, self-consistent EN value so consumers rendered
// outside an <I18nProvider> (e.g. AnalysisReport on the in-app project-analysis
// page) still get fmtMonthYear/tMilestone — a missing fn here white-screens the app.
export const I18nContext = createContext({
  lang: 'en',
  dir: 'ltr',
  t: makeT('en'),
  fmtMonthYear: (d) => fmtMonthYear(d, 'en'),
  tMilestone: (l) => translateMilestone(l, 'en'),
});

export function I18nProvider({ lang = 'en', children }) {
  const dir = lang === 'he' ? 'rtl' : 'ltr';
  const value = { lang, dir, t: makeT(lang), fmtMonthYear: (d) => fmtMonthYear(d, lang), tMilestone: (l) => translateMilestone(l, lang) };
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  return useContext(I18nContext);
}
